<script lang="ts" setup>
import getPage from "@/graphql/page.query.graphql";
import getArticle from "@/graphql/blog-article.query.graphql";
import getReference from "@/graphql/reference.query.graphql";
import getTeam from "@/graphql/team.query.graphql";
import type {
  GetPageQuery,
  GetBlogArticleQuery,
  GetReferenceQuery,
  GetTeamQuery,
} from "@/types/graphql";

const route = useRoute();
const { locale } = useI18n();
const slugs = computed(() => {
  let slug = route.params.slug;
  if (Array.isArray(slug)) {
    slug = slug.filter(Boolean);
  }
  return Array.isArray(slug) ? slug : [slug];
});

// const page = ref<Page | BlogArticle | Reference | undefined | null>(undefined);
const requestType = computed(() =>
  slugs.value.length === 1
    ? "pages"
    : slugs.value.length > 1 &&
        (slugs.value[0] === "blog" || slugs.value[0] === "blog_english")
      ? "blogArticles"
      : slugs.value.length > 1 &&
          (slugs.value[0] === "reference" || slugs.value[0] === "referenzen")
        ? "references"
        : slugs.value.length > 1 && slugs.value[0] === "team"
          ? "teams"
          : "pages",
);

const query = computed(() => {
  if (requestType.value === "pages") return getPage;
  if (requestType.value === "blogArticles") return getArticle;
  if (requestType.value === "references") return getReference;
  if (requestType.value === "teams") return getTeam;
});

// TODO: optimize for only slug query
// Get all pages for route translations
const { data, error } = await useAsyncQuery<GetPageQuery>({
  query: getPage,
  variables: {
    locale: locale.value,
    slug: slugs.value[0],
  },
  key: route.path + locale.value,
});

/**
 * Get the page for the current route
 * based on the slugs array
 * the first param represents the page
 * if there are more than one slug
 * the first param represents the collection
 * the second param represents the collection item
 */
const { data: pageData, error: pageError } = await useAsyncQuery<
  GetBlogArticleQuery | GetReferenceQuery | GetPageQuery | GetTeamQuery
>({
  query: query.value,
  variables: {
    locale: locale.value,
    slug: [...slugs.value].pop(),
  },
  key: route.path + locale.value,
});

const page = computed(() =>
  requestType.value
    ? pageData.value?.[requestType.value]?.data[0]?.attributes
    : undefined,
);

function getLanguageCounterpartSlug() {
  if (requestType.value === "pages") {
    const slug =
      pageData.value.pages?.data[0]?.attributes?.localizations?.data[0]
        ?.attributes?.Slug ?? "";
    return slug;
  } else if (requestType.value === "blogArticles") {
    const slug =
      pageData.value.blogArticles?.data[0]?.attributes?.localizations?.data[0]
        ?.attributes?.slug ?? "";
    return (locale.value !== "de" ? "blog/" : "blog_english/") + slug;
  } else if (requestType.value === "references") {
    const slug =
      pageData.value.references?.data[0]?.attributes?.localizations?.data[0]
        ?.attributes?.slug ?? "";
    return (locale.value !== "de" ? "referenzen/" : "reference/") + slug;
  }
}

const localization = useLocalization();
const languageCounterPartSlug = getLanguageCounterpartSlug();
localization.value[locale.value === "de" ? "en" : "de"].pathMatch = [
  languageCounterPartSlug,
];
/**
 * Handle undefined slugs
 * if the query returns a empty data array
 * it will render error.vue from the root directory
 */
if (pageData.value?.[requestType.value]?.data?.length === 0) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
    message: error.value?.message,
  });
}
watch(
  [error, pageError],
  () => {
    if (error.value || pageError.value) {
      throw createError({
        statusCode: 404,
        statusMessage: "Page not found",
        message: error.value?.message,
      });
    }
  },
  { immediate: true },
);

// const pages = computed(() => data.value);

const footer = computed(() => {
  return data.value?.[requestType.value]?.data[0]?.attributes?.footer;
});

// onResult((result) => {
//   if (
//     !result.data.pages?.data.find((page) => {
//       // if (page.attributes?.Slug === slugs.value[0]) {
//       //   route.meta.nuxtI18n = {
//       //     de: { slug: page.attributes?.Slug },
//       //     en: { slug: page.attributes?.Slug },
//       //   };
//       // }
//       return slugs.value.includes(page.attributes?.Slug);
//     })
//   )
//     throw createError({
//       statusCode: 404,
//       statusMessage: "Page Not Found",
//     });

//   route.meta.nuxtI18n = {
//     de: { slug: ["post-de"] },
//     en: { slug: ["post-en"] },
//   };
// });

// watch(
//   () => page.value,
//   (page) => {
//     if (page) {
//       route.meta.nuxtI18n = {
//         de: { slug: page?.Slug },
//         en: { slug: page?.Slug },
//       };
//     }
//   }
// );

// const head = useLocaleHead({
//   identifierAttribute: "id",
//   addSeoAttributes: true,
// });

// useHead({
//   title: () => page.value?.Name ?? "",
//   link: head.value.link,
//   meta: head.value.meta,
// });

// onActivated(() => {
//   refetch();
//   useHead({
//     title: () => page.value?.Name ?? "",
//   });
// });

</script>
<template>
  <main>
    <template-renderer v-bind="page" />
    <block-footer :headline="footer?.Titel" :link="footer?.link" />
  </main>
</template>
